import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  CircularProgress,
  Alert,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  List,
  ListItem,
  ListItemText,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import {
  PlayArrow as StartIcon,
  Stop as StopIcon,
  Pause as PauseIcon,
  PlayCircle as ContinueIcon,
  Refresh as RefreshIcon,
  Factory as FactoryIcon,
} from '@mui/icons-material';
import {
  productionOrderService,
  ProductionOrderWithProduct,
  ProductionOrderBulkRequirement,
} from '../services/productionOrderService';
import { formatDate, formatTime, formatDateWithoutYear } from '../utils/date';
import ProductionLineSelector from '../components/ProductionLineSelector';
import {
  ProductionLineWithCapacity,
  productionLineService,
} from '../services/productionLineService';

const ProductionDashboard: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const [todaysOrders, setTodaysOrders] = useState<ProductionOrderWithProduct[]>([]);
  const [selectedOrderId, setSelectedOrderId] = useState<number | null>(null);
  const [selectedOrderBulkRequirements, setSelectedOrderBulkRequirements] = useState<
    ProductionOrderBulkRequirement[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [timeState, setTimeState] = useState({
    currentTime: new Date(),
    countdownDisplay: '',
  });
  const [showCompleted, setShowCompleted] = useState(false);
  const [todayOnly, setTodayOnly] = useState(true);

  // Production line filtering states
  const [selectedProductionLineId, setSelectedProductionLineId] = useState<number | null>(null);
  const [_productionLines, setProductionLines] = useState<ProductionLineWithCapacity[]>([]);
  const [loadingLines, setLoadingLines] = useState(false);

  // Pause/Continue states
  const [pauseDialogOpen, setPauseDialogOpen] = useState(false);
  const [pausingOrder, setPausingOrder] = useState<ProductionOrderWithProduct | null>(null);
  const [pauseReason, setPauseReason] = useState('');
  const [pauseDurations, setPauseDurations] = useState<Record<number, number>>({});

  // Dialog states
  const [openStartDialog, setOpenStartDialog] = useState(false);
  const [openCompleteDialog, setOpenCompleteDialog] = useState(false);
  const [startingOrder, setStartingOrder] = useState<ProductionOrderWithProduct | null>(null);
  const [completingOrder, setCompletingOrder] = useState<ProductionOrderWithProduct | null>(null);

  // Start production form data
  const [startFormData, setStartFormData] = useState({
    overrideCases: 0,
  });

  // Complete production form data
  const [completeFormData, setCompleteFormData] = useState({
    actualCasesProduced: 0,
    actualUnitsProduced: 0,
    productionIssues: '',
    issueCategory: '',
    issueSeverity: 'low' as 'low' | 'medium' | 'high',
    bulkUsedNotes: '',
    createNewOrderForLeftover: false,
    newOrderDueDate: '',
    newOrderPriority: 'normal' as 'low' | 'normal' | 'high' | 'urgent',
    newOrderNotes: '',
  });

  // Bulk requirements and usage
  const [bulkRequirements, setBulkRequirements] = useState<ProductionOrderBulkRequirement[]>([]);
  const [bulkUsage, setBulkUsage] = useState<
    Array<{
      bulk_product_id: number;
      bulk_product_name: string;
      bulk_product_sku: string;
      cases_needed: number;
      cases_used: number;
      notes: string;
    }>
  >([]);

  // Get selected order
  const selectedOrder = todaysOrders.find((order) => order.production_order_id === selectedOrderId);

  // Enhanced state management for production orders
  const pauseTimeRef = useRef<Date | null>(null);

  // Enhanced countdown calculation with proper pause handling
  const calculateRemainingTime = useCallback(
    (
      order: ProductionOrderWithProduct,
      now: Date
    ): {
      display: string;
      isOverdue: boolean;
      isPaused: boolean;
      remainingMs: number;
    } => {
      if (!order.actual_start_time || !order.estimated_production_hours) {
        return { display: '', isOverdue: false, isPaused: false, remainingMs: 0 };
      }

      // #region agent log
      fetch('http://127.0.0.1:7242/ingest/7063a634-a1ab-4c2e-9144-95cf090d6708', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          location: 'ProductionDashboard.tsx:131',
          message: 'calculateRemainingTime ENTRY',
          data: {
            orderId: order.production_order_id,
            isPaused: (order as any).is_paused,
            pauseStartTime: (order as any).pause_start_time,
            totalPauseDuration: (order as any).total_pause_duration,
            actualStartTime: order.actual_start_time,
            estimatedHours: order.estimated_production_hours,
          },
          timestamp: Date.now(),
          sessionId: 'debug-session',
          runId: 'run1',
          hypothesisId: 'A',
        }),
      }).catch(() => {});
      // #endregion

      const startTime = new Date(order.actual_start_time);
      const estimatedMs = order.estimated_production_hours * 60 * 60 * 1000;
      const totalPauseMs = ((order as any).total_pause_duration || 0) * 1000;

      // #region agent log
      fetch('http://127.0.0.1:7242/ingest/7063a634-a1ab-4c2e-9144-95cf090d6708', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          location: 'ProductionDashboard.tsx:147',
          message: 'BEFORE calculation',
          data: {
            startTimeMs: startTime.getTime(),
            estimatedMs,
            totalPauseMs,
            calculatedEndTimeMs: startTime.getTime() + estimatedMs + totalPauseMs,
          },
          timestamp: Date.now(),
          sessionId: 'debug-session',
          runId: 'run1',
          hypothesisId: 'A',
        }),
      }).catch(() => {});
      // #endregion

      let referenceTime: Date;
      let remainingMs: number;

      if ((order as any).is_paused) {
        // For paused orders, don't calculate or show time remaining
        // Store the frozen time when first paused
        if ((order as any).pause_start_time) {
          referenceTime = new Date((order as any).pause_start_time);
        } else {
          referenceTime = now;
        }
        if (pauseTimeRef.current === null) {
          pauseTimeRef.current = referenceTime;
        }
        remainingMs = 0; // Not used for paused orders
      } else {
        // For active orders, use current time and reset frozen reference
        referenceTime = now;
        const expectedEndTime = new Date(startTime.getTime() + estimatedMs + totalPauseMs);
        remainingMs = expectedEndTime.getTime() - referenceTime.getTime();

        // #region agent log
        fetch('http://127.0.0.1:7242/ingest/7063a634-a1ab-4c2e-9144-95cf090d6708', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            location: 'ProductionDashboard.tsx:200',
            message: 'ACTIVE calculation',
            data: {
              expectedEndTimeMs: expectedEndTime.getTime(),
              nowMs: now.getTime(),
              remainingMs,
              remainingHours: remainingMs / (1000 * 60 * 60),
            },
            timestamp: Date.now(),
            sessionId: 'debug-session',
            runId: 'run1',
            hypothesisId: 'A',
          }),
        }).catch(() => {});
        // #endregion

        pauseTimeRef.current = null;
      }

      const isPaused = (order as any).is_paused || false;

      // For paused orders, don't show time remaining
      if (isPaused) {
        return {
          display: '', // Empty display for paused orders
          isOverdue: false,
          isPaused: true,
          remainingMs: 0,
        };
      }

      // For active orders, check if overdue
      const isOverdue = remainingMs <= 0;
      if (isOverdue) {
        return {
          display: 'OVERDUE',
          isOverdue: true,
          isPaused: false,
          remainingMs: 0,
        };
      }

      const hours = Math.floor(remainingMs / (1000 * 60 * 60));
      const minutes = Math.floor((remainingMs % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((remainingMs % (1000 * 60)) / 1000);

      const timeDisplay = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;

      return {
        display: timeDisplay,
        isOverdue: false,
        isPaused: false,
        remainingMs,
      };
    },
    []
  );

  // Synchronized timer - updates both clock and countdown atomically
  useEffect(() => {
    const timer = setInterval(() => {
      const now = new Date();

      // Find in-progress order
      let inProgressOrder = null;
      if (selectedOrder && selectedOrder.status === 'in_progress') {
        inProgressOrder = selectedOrder;
      } else {
        inProgressOrder = todaysOrders.find((order) => order.status === 'in_progress') || null;
      }

      // Calculate countdown with the same timestamp
      let countdown = '';
      if (inProgressOrder) {
        const result = calculateRemainingTime(inProgressOrder, now);
        countdown = result.display;
      }

      // Single atomic state update
      setTimeState({
        currentTime: now,
        countdownDisplay: countdown,
      });
    }, 1000);

    // Initial update
    const now = new Date();
    const inProgressOrder =
      selectedOrder?.status === 'in_progress'
        ? selectedOrder
        : todaysOrders.find((o) => o.status === 'in_progress') || null;

    setTimeState({
      currentTime: now,
      countdownDisplay: inProgressOrder ? calculateRemainingTime(inProgressOrder, now).display : '',
    });

    return () => clearInterval(timer);
  }, [selectedOrder, todaysOrders, calculateRemainingTime]);

  // Enhanced function to load relevant production orders
  const loadRelevantOrders = async () => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      // Get today's date in YYYY-MM-DD format without timezone conversion
      const today = new Date();
      const todayYMD = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

      console.log(
        'Loading orders for date:',
        todayYMD,
        'Production Line:',
        selectedProductionLineId
      );

      // Get production orders with API filtering
      // Use a large pageSize to get all relevant orders (backend caps at 100)
      const filters: any = {
        pageSize: 100, // Get up to 100 orders to ensure we see all relevant ones
      };
      if (selectedProductionLineId !== null) {
        filters.productionLineId = selectedProductionLineId;
      }

      const allOrders = await productionOrderService.getAllProductionOrders(filters);

      console.log('Total orders fetched:', allOrders.length);

      // Enhanced filtering logic to show relevant orders
      const relevantOrders = allOrders.filter((order: ProductionOrderWithProduct) => {
        // Helper to check if order is scheduled for today
        const isScheduledForToday = () => {
          // Check scheduled_dates first (comma-separated list)
          if ((order as any).scheduled_dates) {
            const dates = ((order as any).scheduled_dates as string)
              .split(',')
              .map((d: string) => d.trim());
            const result = dates.some((date: string) => date === todayYMD);
            return result;
          }
          // Fall back to planned_production_date
          if (order.planned_production_date) {
            const orderDateStr = order.planned_production_date.split('T')[0];
            const result = orderDateStr === todayYMD;
            return result;
          }
          return false;
        };

        // For in-progress orders - ALWAYS show them regardless of date filter or pause status
        if (order.status === 'in_progress') {
          console.log(`Order ${order.production_order_id}: IN_PROGRESS -> INCLUDE (always shown)`);
          return true;
        }

        // For pending orders
        if (order.status === 'pending') {
          // Helper to get the first scheduled date or planned_production_date
          const getOrderDate = () => {
            if ((order as any).scheduled_dates) {
              const dates = ((order as any).scheduled_dates as string)
                .split(',')
                .map((d: string) => d.trim());
              return dates[0] || null;
            }
            return order.planned_production_date
              ? order.planned_production_date.split('T')[0]
              : null;
          };

          const orderDateStr = getOrderDate();

          // If todayOnly is enabled, only show pending orders scheduled for today
          if (todayOnly) {
            if (!orderDateStr || !isScheduledForToday()) {
              console.log(
                `Order ${order.production_order_id}: PENDING, NOT SCHEDULED TODAY -> SKIP (todayOnly=true)`
              );
              return false;
            }
            console.log(
              `Order ${order.production_order_id}: PENDING, TODAY -> INCLUDE (todayOnly=true)`
            );
            return true;
          }

          // When todayOnly is false, show pending orders
          if (!orderDateStr) {
            console.log(`Order ${order.production_order_id}: PENDING, NO DATE -> INCLUDE`);
            return true;
          }

          try {
            const isScheduledToday = orderDateStr === todayYMD;
            const isOverdue = orderDateStr < todayYMD;

            // When todayOnly is false, show if scheduled for today, overdue, or within the next 30 days

            // Check if scheduled within next 30 days
            const orderDate = new Date(orderDateStr);
            const today = new Date();
            const thirtyDaysFromNow = new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000);
            const isWithinNext30Days = orderDate <= thirtyDaysFromNow;

            if (isScheduledToday || isOverdue || isWithinNext30Days) {
              console.log(
                `Order ${order.production_order_id}: PENDING, ${isOverdue ? 'OVERDUE' : isScheduledToday ? 'TODAY' : 'FUTURE (within 30 days)'} -> INCLUDE`
              );
              return true;
            }

            console.log(
              `Order ${order.production_order_id}: PENDING, FUTURE (beyond 30 days) -> SKIP`
            );
            return false;
          } catch (e) {
            console.error(
              'Error parsing date for order',
              order.production_order_id,
              orderDateStr,
              e
            );
            return !todayOnly; // Include if date parsing fails only when todayOnly is false
          }
        }

        // For completed orders, only show if requested
        if (order.status === 'completed') {
          if (!showCompleted) {
            return false;
          }

          // If showCompleted is true, check if scheduled for today (when todayOnly is enabled)
          if (todayOnly && !isScheduledForToday()) {
            return false;
          }

          console.log(`Order ${order.production_order_id}: COMPLETED -> INCLUDE`);
          return true;
        }

        // Skip cancelled orders and completed orders when not requested
        console.log(`Order ${order.production_order_id}: ${order.status.toUpperCase()} -> SKIP`);
        return false;
      });

      // Production line filtering is now handled by the API

      console.log('Final filtered orders:', relevantOrders.length);

      // Enhanced sorting: in-progress first, then by priority, then by time
      relevantOrders.sort((a: ProductionOrderWithProduct, b: ProductionOrderWithProduct) => {
        // First sort by status (in_progress first, then pending, then completed)
        const statusOrder = { in_progress: 0, pending: 1, completed: 2, cancelled: 3 };
        const aStatusOrder = statusOrder[a.status as keyof typeof statusOrder] ?? 4;
        const bStatusOrder = statusOrder[b.status as keyof typeof statusOrder] ?? 4;

        if (aStatusOrder !== bStatusOrder) {
          return aStatusOrder - bStatusOrder;
        }

        // Then by priority
        const priorityOrder = { urgent: 0, high: 1, normal: 2, low: 3 };
        const aPriority = priorityOrder[a.priority_level as keyof typeof priorityOrder] ?? 4;
        const bPriority = priorityOrder[b.priority_level as keyof typeof priorityOrder] ?? 4;

        if (aPriority !== bPriority) {
          return aPriority - bPriority;
        }

        // Finally by planned time (if available)
        if (a.planned_production_time && b.planned_production_time) {
          return a.planned_production_time.localeCompare(b.planned_production_time);
        }

        // If one has planned time and other doesn't, prioritize the one with planned time
        if (a.planned_production_time && !b.planned_production_time) return -1;
        if (!a.planned_production_time && b.planned_production_time) return 1;

        return 0;
      });

      setTodaysOrders(relevantOrders);
    } catch (err) {
      console.error('Failed to load production orders:', err);
      setError('Failed to load production orders');
    } finally {
      setLoading(false);
    }
  };

  // Load production lines
  const loadProductionLines = async () => {
    try {
      setLoadingLines(true);
      const lines = await productionLineService.getProductionLinesWithWorkload();
      setProductionLines(lines);
    } catch (err) {
      console.error('Failed to load production lines:', err);
      setError('Failed to load production lines');
    } finally {
      setLoadingLines(false);
    }
  };

  // Load relevant production orders on mount and when filters change
  useEffect(() => {
    loadRelevantOrders();
  }, [showCompleted, selectedProductionLineId, todayOnly]);

  // Load production lines on mount
  useEffect(() => {
    loadProductionLines();
  }, []);

  // Initialize production line from URL on mount
  useEffect(() => {
    const productionLineParam = searchParams.get('productionLine');
    if (productionLineParam) {
      const lineId = parseInt(productionLineParam, 10);
      if (!isNaN(lineId)) {
        setSelectedProductionLineId(lineId);
      }
    }
  }, [searchParams]);

  // Update URL when production line changes
  const handleProductionLineChange = (lineId: number | null) => {
    setSelectedProductionLineId(lineId);

    // Update URL
    const newSearchParams = new URLSearchParams(searchParams);
    if (lineId !== null) {
      newSearchParams.set('productionLine', lineId.toString());
    } else {
      newSearchParams.delete('productionLine');
    }
    setSearchParams(newSearchParams);
  };

  const handleOrderSelect = async (orderId: number) => {
    setSelectedOrderId(orderId);

    // Load bulk requirements for selected order
    try {
      const requirements = await productionOrderService.getBulkRequirements(orderId);
      setSelectedOrderBulkRequirements(requirements);
    } catch (err) {
      console.error('Error loading bulk requirements for selected order:', err);
      setSelectedOrderBulkRequirements([]);
    }
  };

  // Enhanced start production handler
  const handleStartProduction = async (order: ProductionOrderWithProduct) => {
    setStartingOrder(order);
    setStartFormData({
      overrideCases: order.quantity_cases,
    });

    // Load bulk requirements
    try {
      const requirements = await productionOrderService.getBulkRequirements(
        order.production_order_id
      );
      setBulkRequirements(requirements);
    } catch (err) {
      console.error('Error loading bulk requirements:', err);
    }

    setOpenStartDialog(true);
  };

  const handleConfirmStartProduction = async () => {
    if (!startingOrder) return;

    try {
      const overrideCases =
        startFormData.overrideCases !== startingOrder.quantity_cases
          ? startFormData.overrideCases
          : undefined;

      await productionOrderService.startProductionOrder(
        startingOrder.production_order_id,
        overrideCases
      );

      setSuccess(`Production order #${startingOrder.production_order_id} started successfully`);
      setOpenStartDialog(false);
      setStartingOrder(null);
      loadRelevantOrders();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to start production order');
    }
  };

  // Enhanced complete production handler
  const handleCompleteProduction = async (order: ProductionOrderWithProduct) => {
    setCompletingOrder(order);

    // Initialize form with calculated values
    setCompleteFormData({
      actualCasesProduced: order.quantity_cases,
      actualUnitsProduced: order.quantity_units,
      productionIssues: '',
      issueCategory: '',
      issueSeverity: 'low',
      bulkUsedNotes: '',
      createNewOrderForLeftover: false,
      newOrderDueDate: '',
      newOrderPriority: order.priority_level,
      newOrderNotes: '',
    });

    // Load bulk requirements and calculate expected usage
    try {
      const requirements = await productionOrderService.getBulkRequirements(
        order.production_order_id
      );
      setBulkRequirements(requirements);

      // Initialize bulk usage with calculated values
      const calculatedUsage = requirements.map((req) => ({
        bulk_product_id: req.bulk_product_id,
        bulk_product_name: req.bulk_product.name,
        bulk_product_sku: req.bulk_product.sku_number,
        cases_needed: req.cases_needed,
        cases_used: req.cases_needed, // Default to expected usage
        notes: '',
      }));
      setBulkUsage(calculatedUsage);
    } catch (err) {
      console.error('Error loading bulk requirements:', err);
    }

    setOpenCompleteDialog(true);
  };

  const handleConfirmCompleteProduction = async () => {
    if (!completingOrder) return;

    try {
      const completionData = {
        actualCasesProduced: completeFormData.actualCasesProduced,
        actualUnitsProduced: completeFormData.actualUnitsProduced,
        productionIssues: completeFormData.productionIssues || undefined,
        bulkUsedNotes: completeFormData.bulkUsedNotes || undefined,
        bulkUsage: bulkUsage.map((usage) => ({
          bulk_product_id: usage.bulk_product_id,
          cases_used: usage.cases_used,
          notes: usage.notes || undefined,
        })),
        createNewOrderForLeftover: completeFormData.createNewOrderForLeftover,
        newOrderDueDate: completeFormData.newOrderDueDate || undefined,
      };

      const result = await productionOrderService.completeProductionOrder(
        completingOrder.production_order_id,
        completionData
      );

      let successMessage = `Production order #${completingOrder.production_order_id} completed successfully`;
      if (result.newOrderId) {
        successMessage += `. New order #${result.newOrderId} created for leftover cases.`;
      }

      setSuccess(successMessage);
      setOpenCompleteDialog(false);
      setCompletingOrder(null);
      loadRelevantOrders();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to complete production order');
    }
  };

  // Pause/Continue handlers
  const handlePauseProduction = (order: ProductionOrderWithProduct) => {
    setPausingOrder(order);
    setPauseReason('');
    setPauseDialogOpen(true);
  };

  const handleConfirmPause = async () => {
    if (!pausingOrder) return;

    try {
      await productionOrderService.pauseProductionOrder(
        pausingOrder.production_order_id,
        pauseReason || undefined
      );

      setSuccess(`Production order #${pausingOrder.production_order_id} paused successfully`);
      setPauseDialogOpen(false);
      setPausingOrder(null);
      setPauseReason('');
      loadRelevantOrders();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to pause production order');
    }
  };

  const handleContinueProduction = async (order: ProductionOrderWithProduct) => {
    try {
      await productionOrderService.continueProductionOrder(order.production_order_id);

      setSuccess(`Production order #${order.production_order_id} resumed successfully`);
      loadRelevantOrders();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to resume production order');
    }
  };

  // Format pause duration in seconds to readable format
  const formatPauseDuration = (seconds: number): string => {
    // Handle negative or invalid values
    if (seconds < 0 || isNaN(seconds)) {
      return '0s';
    }

    if (seconds < 60) {
      return `${seconds}s`;
    } else if (seconds < 3600) {
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = seconds % 60;
      return remainingSeconds > 0 ? `${minutes}m ${remainingSeconds}s` : `${minutes}m`;
    } else {
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
    }
  };

  // Update pause durations for paused orders
  useEffect(() => {
    const updatePauseDurations = async () => {
      const pausedOrders = todaysOrders.filter(
        (order) => order.status === 'in_progress' && (order as any).is_paused
      );

      if (pausedOrders.length > 0) {
        const newDurations: Record<number, number> = {};

        for (const order of pausedOrders) {
          try {
            const duration = await productionOrderService.getCurrentPauseDuration(
              order.production_order_id
            );
            newDurations[order.production_order_id] = duration;
          } catch (err) {
            console.error('Error fetching pause duration:', err);
          }
        }

        setPauseDurations(newDurations);
      }
    };

    // Update pause durations every 15 seconds for paused orders
    // (reduced frequency to avoid rate limiting)
    const interval = setInterval(updatePauseDurations, 15000);
    updatePauseDurations(); // Initial update

    return () => clearInterval(interval);
  }, [todaysOrders]);

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'low':
        return 'success';
      case 'normal':
        return 'default';
      case 'high':
        return 'warning';
      case 'urgent':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'warning';
      case 'in_progress':
        return 'info';
      case 'completed':
        return 'success';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  const formatQuantity = (cases: number, units: number) => {
    if (cases > 0 && units > 0) {
      return `${cases} cases + ${units} units`;
    } else if (cases > 0) {
      return `${cases} cases`;
    } else if (units > 0) {
      return `${units} units`;
    }
    return '0';
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={3}>
        <Box sx={{ flex: 1 }}>
          <Box display="flex" alignItems="center" gap={2} flexWrap="wrap" mb={1}>
            <Typography variant="h4" component="h1">
              Production Floor Dashboard
            </Typography>
            {/* Filter Section - aligned with title */}
            <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
              <Box display="flex" alignItems="center" gap={0.5}>
                <Typography variant="body2" color="text.secondary">
                  Production Line:
                </Typography>
                <Box sx={{ minWidth: 150 }}>
                  <ProductionLineSelector
                    selectedLineId={selectedProductionLineId}
                    onLineChange={handleProductionLineChange}
                    showAllOption={true}
                    label=""
                    disabled={loadingLines}
                  />
                </Box>
              </Box>
              <Box sx={{ mx: 0.25 }}>|</Box>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={showCompleted}
                    onChange={(e) => setShowCompleted(e.target.checked)}
                    size="small"
                  />
                }
                label="Show Completed"
                sx={{ m: 0 }}
              />
              <Box sx={{ mx: 0.5 }}>|</Box>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={todayOnly}
                    onChange={(e) => setTodayOnly(e.target.checked)}
                    size="small"
                  />
                }
                label="Today only"
                sx={{ m: 0 }}
              />
            </Box>
          </Box>
        </Box>

        {/* Countdown Clock and Date/Time */}
        <Box display="flex" alignItems="flex-start" gap={2}>
          {/* Countdown Clock */}
          {timeState.countdownDisplay && (
            <Box
              sx={{
                textAlign: 'center',
                p: 2,
                border: '2px solid',
                borderColor:
                  timeState.countdownDisplay === 'OVERDUE' ? 'error.main' : 'primary.main',
                borderRadius: 2,
                bgcolor: timeState.countdownDisplay === 'OVERDUE' ? 'error.light' : 'primary.light',
                minWidth: 200,
                display: 'flex',
                flexDirection: 'column',
                height: '104px',
              }}
            >
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                Time Remaining {selectedOrder && `for Order #${selectedOrder.production_order_id}`}
                {(selectedOrder as any)?.is_paused && ' (PAUSED)'}
              </Typography>
              <Typography
                variant="h3"
                component="div"
                sx={{
                  fontFamily: 'monospace',
                  fontWeight: 'bold',
                  color: 'white',
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {timeState.countdownDisplay}
              </Typography>
            </Box>
          )}

          {/* Date and Time Display */}
          <Box
            sx={{
              textAlign: 'center',
              p: 2,
              border: '2px solid',
              borderColor: 'grey.300',
              borderRadius: 2,
              bgcolor: 'grey.50',
              minWidth: 200,
              height: '104px',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {/* Date on top - matching "Time Remaining" label */}
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              {timeState.currentTime.toLocaleDateString('en-US', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
            </Typography>
            {/* Time on bottom - matching countdown size */}
            <Typography
              variant="h3"
              component="div"
              sx={{
                fontFamily: 'monospace',
                fontWeight: 'bold',
                color: 'primary.main',
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {timeState.currentTime.toLocaleTimeString('en-US', {
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                hour12: true,
              })}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Main Content */}
      <Box sx={{ height: 'calc(100vh - 250px)', display: 'flex', gap: 3 }}>
        {/* Left Side - Orders List */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          <Paper sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box
              sx={{
                p: 2,
                borderBottom: '1px solid #e0e0e0',
                flexShrink: 0,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Typography variant="h6">Production Orders</Typography>
              <Button
                variant="outlined"
                size="small"
                startIcon={<RefreshIcon />}
                onClick={loadRelevantOrders}
                sx={{ fontSize: '0.75rem', minWidth: 'auto', px: 1 }}
              >
                Refresh
              </Button>
            </Box>
            <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
              {todaysOrders.map((order) => (
                <Card
                  key={order.production_order_id}
                  sx={{
                    mb: 2,
                    cursor: 'pointer',
                    border:
                      selectedOrderId === order.production_order_id
                        ? '2px solid #1976d2'
                        : '1px solid #e0e0e0',
                    '&:hover': {
                      boxShadow: 3,
                      borderColor: '#1976d2',
                    },
                  }}
                  onClick={() => handleOrderSelect(order.production_order_id)}
                >
                  <CardContent>
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="flex-start"
                      mb={2}
                    >
                      <Box>
                        <Typography variant="h6" component="h3">
                          #{order.production_order_id} - {order.product_name}
                          {(order as any).current_portion_number && (
                            <Typography
                              component="span"
                              variant="body2"
                              sx={{ ml: 1, color: 'text.secondary' }}
                            >
                              (Portion {(order as any).current_portion_number})
                            </Typography>
                          )}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {order.product_code}
                          {(order as any).current_portion_scheduled_date &&
                            (order as any).current_portion_scheduled_time && (
                              <Typography component="span" variant="body2" sx={{ ml: 1 }}>
                                •{' '}
                                {formatDateWithoutYear(
                                  (order as any).current_portion_scheduled_date
                                )}{' '}
                                {formatTime((order as any).current_portion_scheduled_time)}
                              </Typography>
                            )}
                        </Typography>
                      </Box>
                      <Stack direction="row" spacing={1}>
                        <Chip
                          label={order.status.replace('_', ' ').toUpperCase()}
                          color={getStatusColor(order.status) as any}
                          size="small"
                        />
                        <Chip
                          label={order.priority_level.toUpperCase()}
                          color={getPriorityColor(order.priority_level) as any}
                          size="small"
                        />
                      </Stack>
                    </Box>

                    <Grid container spacing={2}>
                      <Grid item xs={4}>
                        <Typography variant="body2" color="text.secondary">
                          Quantity
                        </Typography>
                        <Typography variant="body1">
                          {formatQuantity(order.quantity_cases, order.quantity_units)}
                        </Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography variant="body2" color="text.secondary">
                          Due Date
                        </Typography>
                        <Typography variant="body1">
                          {order.due_date ? formatDate(order.due_date) : 'Not set'}
                        </Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography variant="body2" color="text.secondary">
                          Scheduled
                        </Typography>
                        <Typography variant="body1">
                          {(order as any).current_portion_scheduled_date &&
                          (order as any).current_portion_scheduled_time
                            ? `${formatDateWithoutYear((order as any).current_portion_scheduled_date)} ${formatTime((order as any).current_portion_scheduled_time)}`
                            : (order as any).current_portion_scheduled_date
                              ? formatDateWithoutYear((order as any).current_portion_scheduled_date)
                              : order.planned_production_date && order.planned_production_time
                                ? `${formatDateWithoutYear(order.planned_production_date)} ${formatTime(order.planned_production_time)}`
                                : order.planned_production_date
                                  ? formatDateWithoutYear(order.planned_production_date)
                                  : 'Not set'}
                        </Typography>
                      </Grid>
                      {order.actual_start_time && (
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">
                            Started
                          </Typography>
                          <Typography variant="body1">
                            {new Date(order.actual_start_time).toLocaleString()}
                          </Typography>
                        </Grid>
                      )}
                      {order.batch_number && (
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">
                            Batch Number
                          </Typography>
                          <Typography variant="body1">{order.batch_number}</Typography>
                        </Grid>
                      )}
                    </Grid>
                  </CardContent>
                </Card>
              ))}

              {todaysOrders.length === 0 && (
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                  <FactoryIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    No Production Orders Scheduled
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    There are no orders scheduled for production today.
                  </Typography>
                </Paper>
              )}
            </Box>
          </Paper>
        </Box>

        {/* Right Side - Order Details */}
        <Box sx={{ width: '50%', display: 'flex', flexDirection: 'column' }}>
          <Paper sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0', flexShrink: 0 }}>
              <Typography variant="h6">Order Details</Typography>
            </Box>
            <Box sx={{ flex: 1, overflowY: 'auto', p: 3 }}>
              {selectedOrder ? (
                <>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h6">Order #{selectedOrder.production_order_id}</Typography>
                    <Stack direction="row" spacing={1}>
                      {selectedOrder.status === 'pending' && (
                        <Button
                          variant="contained"
                          color="success"
                          size="small"
                          startIcon={<StartIcon />}
                          onClick={() => handleStartProduction(selectedOrder)}
                        >
                          Start Production
                        </Button>
                      )}
                      {selectedOrder.status === 'in_progress' && (
                        <>
                          {(selectedOrder as any).is_paused ? (
                            <Button
                              variant="contained"
                              color="success"
                              size="small"
                              startIcon={<ContinueIcon />}
                              onClick={() => handleContinueProduction(selectedOrder)}
                            >
                              Continue Production
                            </Button>
                          ) : (
                            <Button
                              variant="contained"
                              color="warning"
                              size="small"
                              startIcon={<PauseIcon />}
                              onClick={() => handlePauseProduction(selectedOrder)}
                            >
                              Pause Production
                            </Button>
                          )}
                          <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            startIcon={<StopIcon />}
                            onClick={() => handleCompleteProduction(selectedOrder)}
                          >
                            Complete Production
                          </Button>
                        </>
                      )}
                    </Stack>
                  </Box>
                  <Divider sx={{ mb: 2 }} />

                  <List>
                    <ListItem>
                      <ListItemText
                        primary="Product"
                        secondary={`${selectedOrder.product_name} (${selectedOrder.product_code})`}
                      />
                    </ListItem>
                    <Divider />
                    <ListItem>
                      <ListItemText
                        primary="Quantity"
                        secondary={formatQuantity(
                          selectedOrder.quantity_cases,
                          selectedOrder.quantity_units
                        )}
                      />
                    </ListItem>
                    <Divider />
                    {selectedOrder.production_line_name && (
                      <>
                        <ListItem>
                          <ListItemText
                            primary="Production Line"
                            secondary={selectedOrder.production_line_name}
                          />
                        </ListItem>
                        <Divider />
                      </>
                    )}
                    {(selectedOrder as any).current_portion_number && (
                      <>
                        <ListItem>
                          <ListItemText
                            primary="Current Portion"
                            secondary={`Portion ${(selectedOrder as any).current_portion_number}${
                              (selectedOrder as any).current_portion_scheduled_date &&
                              (selectedOrder as any).current_portion_scheduled_time
                                ? ` - ${formatDateWithoutYear((selectedOrder as any).current_portion_scheduled_date)} ${formatTime((selectedOrder as any).current_portion_scheduled_time)}`
                                : (selectedOrder as any).current_portion_scheduled_date
                                  ? ` - ${formatDateWithoutYear((selectedOrder as any).current_portion_scheduled_date)}`
                                  : ''
                            }`}
                          />
                        </ListItem>
                        <Divider />
                      </>
                    )}
                    {(selectedOrder as any).scheduled_dates && (
                      <>
                        <ListItem>
                          <ListItemText
                            primary="Scheduled Dates"
                            secondary={((selectedOrder as any).scheduled_dates as string)
                              .split(',')
                              .map((date: string) => formatDateWithoutYear(date.trim()))
                              .join(', ')}
                          />
                        </ListItem>
                        <Divider />
                      </>
                    )}
                    {(selectedOrder.planned_production_date ||
                      selectedOrder.planned_production_time) &&
                      !(selectedOrder as any).scheduled_dates && (
                        <>
                          <ListItem>
                            <ListItemText
                              primary="Scheduled"
                              secondary={
                                selectedOrder.planned_production_date &&
                                selectedOrder.planned_production_time
                                  ? `${formatDateWithoutYear(selectedOrder.planned_production_date)} ${formatTime(selectedOrder.planned_production_time)}`
                                  : selectedOrder.planned_production_date
                                    ? formatDateWithoutYear(selectedOrder.planned_production_date)
                                    : selectedOrder.planned_production_time
                                      ? formatTime(selectedOrder.planned_production_time)
                                      : 'Not set'
                              }
                            />
                          </ListItem>
                          <Divider />
                        </>
                      )}
                    {selectedOrder.estimated_production_hours && (
                      <>
                        <ListItem>
                          <ListItemText
                            primary="Estimated Hours"
                            secondary={`${selectedOrder.estimated_production_hours} hours`}
                          />
                        </ListItem>
                        <Divider />
                      </>
                    )}
                    {selectedOrder.batch_number && (
                      <>
                        <ListItem>
                          <ListItemText
                            primary="Batch Number"
                            secondary={selectedOrder.batch_number}
                          />
                        </ListItem>
                        <Divider />
                      </>
                    )}
                    {selectedOrder.actual_start_time && (
                      <>
                        <ListItem>
                          <ListItemText
                            primary="Started"
                            secondary={new Date(selectedOrder.actual_start_time).toLocaleString()}
                          />
                        </ListItem>
                        <Divider />
                      </>
                    )}
                    {/* Show pause duration if order has been paused (either currently paused or has pause history) */}
                    {selectedOrder.status === 'in_progress' &&
                      ((selectedOrder as any).is_paused ||
                        (selectedOrder as any).total_pause_duration > 0) && (
                        <>
                          <ListItem>
                            <ListItemText
                              primary="Pause Duration"
                              secondary={
                                (selectedOrder as any).is_paused
                                  ? pauseDurations[selectedOrder.production_order_id]
                                    ? formatPauseDuration(
                                        pauseDurations[selectedOrder.production_order_id]
                                      )
                                    : 'Calculating...'
                                  : formatPauseDuration(
                                      (selectedOrder as any).total_pause_duration || 0
                                    )
                              }
                            />
                          </ListItem>
                          <Divider />
                        </>
                      )}
                    {selectedOrder.notes && (
                      <>
                        <ListItem>
                          <ListItemText primary="Notes" secondary={selectedOrder.notes} />
                        </ListItem>
                        <Divider />
                      </>
                    )}
                  </List>

                  {/* Bulk Requirements Section */}
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    Required Bulk Products
                  </Typography>
                  {selectedOrderBulkRequirements.length > 0 ? (
                    <TableContainer component={Paper} sx={{ mt: 1 }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Bulk Product</TableCell>
                            <TableCell>SKU</TableCell>
                            <TableCell align="right">Cases Needed</TableCell>
                            {selectedOrderBulkRequirements.some(
                              (req) => req.cases_used != null
                            ) && <TableCell align="right">Cases Used</TableCell>}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {selectedOrderBulkRequirements.map((req) => (
                            <TableRow key={req.bulk_product_id}>
                              <TableCell>{req.bulk_product.name}</TableCell>
                              <TableCell>{req.bulk_product.sku_number}</TableCell>
                              <TableCell align="right">{req.cases_needed}</TableCell>
                              {selectedOrderBulkRequirements.some((r) => r.cases_used != null) && (
                                <TableCell align="right">
                                  {req.cases_used != null ? req.cases_used : ''}
                                </TableCell>
                              )}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'grey.50' }}>
                      <Typography variant="body2" color="text.secondary">
                        No bulk products required for this order.
                      </Typography>
                    </Paper>
                  )}
                </>
              ) : (
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  py={8}
                >
                  <FactoryIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    Select a Production Order
                  </Typography>
                  <Typography variant="body2" color="text.secondary" textAlign="center">
                    Click on a production order from the list to view detailed information and
                    manage the order status.
                  </Typography>
                </Box>
              )}
            </Box>
          </Paper>
        </Box>
      </Box>

      {/* Start Production Dialog */}
      <Dialog
        open={openStartDialog}
        onClose={() => setOpenStartDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Start Production Order #{startingOrder?.production_order_id}</DialogTitle>
        <DialogContent>
          {startingOrder && (
            <Grid container spacing={3} sx={{ mt: 1 }}>
              {/* Order Summary */}
              <Grid item xs={12}>
                <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                  <Typography variant="h6" gutterBottom>
                    Order Summary
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Product
                      </Typography>
                      <Typography variant="body1">
                        {startingOrder.product_name} ({startingOrder.product_code})
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Original Quantity
                      </Typography>
                      <Typography variant="body1">
                        {formatQuantity(startingOrder.quantity_cases, startingOrder.quantity_units)}
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>

              {/* Override Cases */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Production Quantity
                </Typography>
                <TextField
                  fullWidth
                  label="Cases to Produce"
                  type="number"
                  value={startFormData.overrideCases}
                  onChange={(e) =>
                    setStartFormData({ ...startFormData, overrideCases: Number(e.target.value) })
                  }
                  helperText="Override the number of cases to produce (optional)"
                />
              </Grid>

              {/* Bulk Requirements */}
              {bulkRequirements.length > 0 && (
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    Required Bulk Products
                  </Typography>
                  <TableContainer component={Paper}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Bulk Product</TableCell>
                          <TableCell>SKU</TableCell>
                          <TableCell align="right">Cases Needed</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {bulkRequirements.map((req) => (
                          <TableRow key={req.bulk_product_id}>
                            <TableCell>{req.bulk_product.name}</TableCell>
                            <TableCell>{req.bulk_product.sku_number}</TableCell>
                            <TableCell align="right">{req.cases_needed}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>
              )}

              {/* Start Confirmation */}
              <Grid item xs={12}>
                <Alert severity="info">
                  <Typography variant="body2">Starting this production order will:</Typography>
                  <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                    <li>Generate a unique batch number</li>
                    <li>Record the start time automatically</li>
                    <li>Change status to "In Progress"</li>
                    {startFormData.overrideCases !== startingOrder.quantity_cases && (
                      <li>Override quantity to {startFormData.overrideCases} cases</li>
                    )}
                  </ul>
                </Alert>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenStartDialog(false)}>Cancel</Button>
          <Button
            onClick={handleConfirmStartProduction}
            variant="contained"
            color="success"
            startIcon={<StartIcon />}
          >
            Start Production
          </Button>
        </DialogActions>
      </Dialog>

      {/* Complete Production Dialog */}
      <Dialog
        open={openCompleteDialog}
        onClose={() => setOpenCompleteDialog(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>Complete Production Order #{completingOrder?.production_order_id}</DialogTitle>
        <DialogContent>
          {completingOrder && (
            <Grid container spacing={3} sx={{ mt: 1 }}>
              {/* Production Results */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Production Results
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="Actual Cases Produced"
                      type="number"
                      value={completeFormData.actualCasesProduced}
                      onChange={(e) =>
                        setCompleteFormData({
                          ...completeFormData,
                          actualCasesProduced: Number(e.target.value),
                        })
                      }
                      required
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="Actual Units Produced"
                      type="number"
                      value={completeFormData.actualUnitsProduced}
                      onChange={(e) =>
                        setCompleteFormData({
                          ...completeFormData,
                          actualUnitsProduced: Number(e.target.value),
                        })
                      }
                      required
                    />
                  </Grid>
                </Grid>
              </Grid>

              {/* Production Issues */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Production Issues (Optional)
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <FormControl fullWidth>
                      <InputLabel>Issue Category</InputLabel>
                      <Select
                        value={completeFormData.issueCategory}
                        onChange={(e) =>
                          setCompleteFormData({
                            ...completeFormData,
                            issueCategory: e.target.value,
                          })
                        }
                        label="Issue Category"
                      >
                        <MenuItem value="">No Issues</MenuItem>
                        <MenuItem value="equipment">Equipment</MenuItem>
                        <MenuItem value="materials">Materials</MenuItem>
                        <MenuItem value="quality">Quality</MenuItem>
                        <MenuItem value="staffing">Staffing</MenuItem>
                        <MenuItem value="other">Other</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={6}>
                    <FormControl fullWidth>
                      <InputLabel>Issue Severity</InputLabel>
                      <Select
                        value={completeFormData.issueSeverity}
                        onChange={(e) =>
                          setCompleteFormData({
                            ...completeFormData,
                            issueSeverity: e.target.value as 'low' | 'medium' | 'high',
                          })
                        }
                        label="Issue Severity"
                      >
                        <MenuItem value="low">Low</MenuItem>
                        <MenuItem value="medium">Medium</MenuItem>
                        <MenuItem value="high">High</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Issue Description"
                      multiline
                      rows={3}
                      value={completeFormData.productionIssues}
                      onChange={(e) =>
                        setCompleteFormData({
                          ...completeFormData,
                          productionIssues: e.target.value,
                        })
                      }
                      placeholder="Describe any issues that occurred during production..."
                    />
                  </Grid>
                </Grid>
              </Grid>

              {/* Bulk Usage */}
              {bulkUsage.length > 0 && (
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    Bulk Product Usage
                  </Typography>
                  <TableContainer component={Paper}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Bulk Product</TableCell>
                          <TableCell>SKU</TableCell>
                          <TableCell align="right">Expected</TableCell>
                          <TableCell align="right">Actual Used</TableCell>
                          <TableCell>Notes</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {bulkUsage.map((usage, index) => (
                          <TableRow key={usage.bulk_product_id}>
                            <TableCell>{usage.bulk_product_name}</TableCell>
                            <TableCell>{usage.bulk_product_sku}</TableCell>
                            <TableCell align="right">{usage.cases_needed}</TableCell>
                            <TableCell align="right">
                              <TextField
                                type="number"
                                size="small"
                                value={usage.cases_used}
                                onChange={(e) => {
                                  const newUsage = [...bulkUsage];
                                  newUsage[index].cases_used = Number(e.target.value);
                                  setBulkUsage(newUsage);
                                }}
                                inputProps={{
                                  step: '0.001',
                                  min: 0,
                                }}
                                sx={{ width: 120 }}
                              />
                            </TableCell>
                            <TableCell>
                              <TextField
                                size="small"
                                value={usage.notes}
                                onChange={(e) => {
                                  const newUsage = [...bulkUsage];
                                  newUsage[index].notes = e.target.value;
                                  setBulkUsage(newUsage);
                                }}
                                placeholder="Notes..."
                                sx={{ width: 120 }}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <TextField
                    fullWidth
                    label="Bulk Usage Summary Notes"
                    multiline
                    rows={2}
                    value={completeFormData.bulkUsedNotes}
                    onChange={(e) =>
                      setCompleteFormData({
                        ...completeFormData,
                        bulkUsedNotes: e.target.value,
                      })
                    }
                    placeholder="Overall notes about bulk product usage..."
                    sx={{ mt: 2 }}
                  />
                </Grid>
              )}

              {/* Leftover Cases */}
              {completeFormData.actualCasesProduced < completingOrder.quantity_cases && (
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    Leftover Cases
                  </Typography>
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    You produced {completeFormData.actualCasesProduced} cases but the order was for{' '}
                    {completingOrder.quantity_cases} cases. There are{' '}
                    {completingOrder.quantity_cases - completeFormData.actualCasesProduced} cases
                    remaining.
                  </Alert>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={completeFormData.createNewOrderForLeftover}
                        onChange={(e) =>
                          setCompleteFormData({
                            ...completeFormData,
                            createNewOrderForLeftover: e.target.checked,
                          })
                        }
                      />
                    }
                    label="Create new production order for leftover cases"
                  />
                  {completeFormData.createNewOrderForLeftover && (
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          label="Due Date for New Order"
                          type="date"
                          value={completeFormData.newOrderDueDate}
                          onChange={(e) =>
                            setCompleteFormData({
                              ...completeFormData,
                              newOrderDueDate: e.target.value,
                            })
                          }
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <FormControl fullWidth>
                          <InputLabel>Priority for New Order</InputLabel>
                          <Select
                            value={completeFormData.newOrderPriority}
                            onChange={(e) =>
                              setCompleteFormData({
                                ...completeFormData,
                                newOrderPriority: e.target.value as any,
                              })
                            }
                            label="Priority for New Order"
                          >
                            <MenuItem value="low">Low</MenuItem>
                            <MenuItem value="normal">Normal</MenuItem>
                            <MenuItem value="high">High</MenuItem>
                            <MenuItem value="urgent">Urgent</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Notes for New Order"
                          value={completeFormData.newOrderNotes}
                          onChange={(e) =>
                            setCompleteFormData({
                              ...completeFormData,
                              newOrderNotes: e.target.value,
                            })
                          }
                          placeholder="Notes for the new production order..."
                        />
                      </Grid>
                    </Grid>
                  )}
                </Grid>
              )}

              {/* Completion Summary */}
              <Grid item xs={12}>
                <Alert severity="info">
                  <Typography variant="body2">Completing this production order will:</Typography>
                  <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                    <li>Record the end time automatically</li>
                    <li>Change status to "Completed"</li>
                    <li>
                      Add {completeFormData.actualCasesProduced} cases and{' '}
                      {completeFormData.actualUnitsProduced} units to inventory
                    </li>
                    <li>Record bulk product usage</li>
                    {completeFormData.createNewOrderForLeftover && (
                      <li>
                        Create a new production order for{' '}
                        {completingOrder.quantity_cases - completeFormData.actualCasesProduced}{' '}
                        leftover cases
                      </li>
                    )}
                  </ul>
                </Alert>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCompleteDialog(false)}>Cancel</Button>
          <Button
            onClick={handleConfirmCompleteProduction}
            variant="contained"
            color="primary"
            startIcon={<StopIcon />}
          >
            Complete Production
          </Button>
        </DialogActions>
      </Dialog>

      {/* Pause Production Dialog */}
      <Dialog
        open={pauseDialogOpen}
        onClose={() => setPauseDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Pause Production Order #{pausingOrder?.production_order_id}</DialogTitle>
        <DialogContent>
          {pausingOrder && (
            <Grid container spacing={3} sx={{ mt: 1 }}>
              {/* Order Summary */}
              <Grid item xs={12}>
                <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                  <Typography variant="h6" gutterBottom>
                    Order Summary
                  </Typography>
                  <Typography variant="body1">
                    {pausingOrder.product_name} ({pausingOrder.product_code})
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {formatQuantity(pausingOrder.quantity_cases, pausingOrder.quantity_units)}
                  </Typography>
                </Paper>
              </Grid>

              {/* Pause Reason */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Pause Reason (Optional)"
                  multiline
                  rows={3}
                  value={pauseReason}
                  onChange={(e) => setPauseReason(e.target.value)}
                  placeholder="Enter the reason for pausing production (e.g., equipment maintenance, material shortage, break time, etc.)"
                  helperText="This information will be logged for tracking and reporting purposes"
                />
              </Grid>

              {/* Pause Information */}
              <Grid item xs={12}>
                <Alert severity="info">
                  <Typography variant="body2">Pausing this production order will:</Typography>
                  <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                    <li>Record the pause start time</li>
                    <li>Track the total pause duration</li>
                    <li>Log the pause action in the audit trail</li>
                    <li>Allow production to be resumed later</li>
                  </ul>
                </Alert>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPauseDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleConfirmPause}
            variant="contained"
            color="warning"
            startIcon={<PauseIcon />}
          >
            Pause Production
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProductionDashboard;
